import { storeToRefs } from "pinia";
import { type ComputedRef, computed, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  type SocialPlatformPostable,
  MINUTE_IN_MILLISECONDS,
  v1BaseUrl,
} from "shared/constants";
import getAppConfig from "shared/helpers/useAppConfig";
import useUserStore from "shared/stores/user";
import { type AuthToken } from "shared/stores/user/types";

import { type PollWindowCloseProps } from "./types";

const TIMEOUT = MINUTE_IN_MILLISECONDS;

export const USE_WINDOW_PARAMS =
  "location=no,width=800,height=800,alwaysRaised=yes";

export const useWindowTitle = (
  platform: Extract<SocialPlatformPostable, "LinkedIn" | "Twitter">
): ComputedRef<string> => {
  const appConfig = getAppConfig();

  return computed(() =>
    getLocaleText(
      `social_engage_add_${platform.toLowerCase()}_account.authorise_platform`,
      {
        platformName: appConfig.value.platformName,
      }
    )
  );
};

export const useValidEvent =
  (storageKey: string) => (storageEvent: StorageEvent) =>
    storageEvent.key === storageKey && Boolean(storageEvent.newValue);

export const useGetAuthUrl =
  (platform: SocialPlatformPostable) => (oauthToken: AuthToken) => {
    const token =
      typeof oauthToken === "string" ? oauthToken : oauthToken.token;

    const base = platform === "LinkedIn" ? "oauth" : "authorize";

    return `${v1BaseUrl}/social_integrations/${platform.toLowerCase()}/${base}?auth_token=${token}`;
  };

export const useOpenAuthWindow = (
  platform: Extract<SocialPlatformPostable, "LinkedIn" | "Twitter">
) => {
  const windowTitle = useWindowTitle(platform);

  const getAuthUrl = useGetAuthUrl(platform);
  const { token } = storeToRefs(useUserStore());

  return () =>
    window.open(getAuthUrl(token.value), windowTitle.value, USE_WINDOW_PARAMS);
};

export const usePollWindowClose = ({
  openedWindow,
  storageKey,
}: PollWindowCloseProps) => {
  const isProcessing = ref(false);
  const timeoutId = ref(0);
  const userExitedLoginProcess = ref(false);
  const windowClosedByAuth = ref(false);
  const windowIntervalId = ref(0);
  const abortController = ref(new AbortController());

  const cleanup = () => {
    clearInterval(windowIntervalId.value);
    isProcessing.value = false;
    localStorage.removeItem(storageKey);
    windowClosedByAuth.value = false;
  };

  return {
    cleanup,
    isProcessing,
    timeoutId,
    userExitedLoginProcess,
    abortController,
    windowClosedByAuth,
    windowIntervalId,
    pollForWindowClose: (): Promise<never> =>
      new Promise((_, reject) => {
        timeoutId.value = window.setTimeout(() => {
          clearInterval(windowIntervalId.value);
          cleanup();
          openedWindow.value?.close();

          reject(
            new Error(getLocaleText("social_engage_oauth.failed_message"))
          );
        }, TIMEOUT);

        windowIntervalId.value = window.setInterval(() => {
          if (openedWindow.value?.closed) {
            clearInterval(windowIntervalId.value);
            clearTimeout(timeoutId.value);

            if (!windowClosedByAuth.value) {
              cleanup();
              abortController.value.abort();
              userExitedLoginProcess.value = true;
              // eslint-disable-next-line prefer-promise-reject-errors
              reject(null);
            }
          }

          openedWindow.value?.focus();
        }, 500);
      }),
  };
};
